'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { unique } from '@lokalise/universal-ts-utils/array/unique'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod/v4'
import { MarketoForm } from '@/app/[locale]/blog/_components/MarketoForm'
import { Tag } from '@/components/elementary/Tag'
import { AppField } from '@/components/forms/AppField'
import { AppForm } from '@/components/forms/AppForm'
import { Close } from '@/components/icons/Close'
import { Search } from '@/components/icons/Search'
import { ChangelogItem } from '@/components/page-builder/global/changelog/ChangelogItem'
import Heading from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getAllCategories, MARKETO_FORM_IDS } from '@/lib/constants'
import { formatDate } from '@/lib/dates'
import { cn } from '@/lib/styles'
import { CHANGELOG_TEST_IDS } from '@/lib/test-ids'
import type { ServerRenderedEntry } from '@/types/helpers'

export const ChangelogFilters = ({
	entries,
	categories,
}: {
	readonly categories: Data.ContentType<'api::changelog-category.changelog-category'>[]
	readonly entries: ServerRenderedEntry<Data.ContentType<'api::change-log.change-log'>>[]
}) => {
	const t = useTranslations('changelog')
	const form = useForm<z.infer<FormSchemaType>>({
		resolver: zodResolver(FilterFormSchema),
		mode: 'onBlur',
		reValidateMode: 'onSubmit',
	})
	const selectedCategory = form.watch('category')
	const selectedDate = form.watch('date')
	const searchTerm = form.watch('search')?.toLowerCase() || ''

	const filteredDates = unique(
		entries
			.filter((entry) => entry.changeDate !== null && entry.changeDate !== undefined)
			.sort((a, b) => new Date(b.changeDate ?? 0).getTime() - new Date(a.changeDate ?? 0).getTime())
			.map((entry) => formatDate(entry.changeDate as string | Date, 'MMMM yyyy')),
	)

	const changelogsFilteredByCategory = useMemo(() => {
		if (!selectedCategory || selectedCategory.id === 0) return entries

		return entries.filter((entry) =>
			entry?.categories?.some((category) => category?.id === selectedCategory?.id),
		)
	}, [selectedCategory, entries])

	const filteredChangelogs = useMemo(() => {
		let result = changelogsFilteredByCategory

		if (searchTerm) {
			result = result.filter((entry) => entry?.htmlContent?.toLowerCase().includes(searchTerm))
		}

		if (selectedDate && selectedDate !== 'all') {
			result = result.filter((entry) => {
				const formattedDate = formatDate(entry.changeDate as string | Date, 'MMMM yyyy')
				return formattedDate === selectedDate
			})
		}

		return result
	}, [changelogsFilteredByCategory, searchTerm, selectedDate])

	const allCategories = getAllCategories(t('allCategoriesTag'))

	return (
		<div
			className="flex lg:flex-row flex-col-reverse lg:gap-17"
			data-test-id={CHANGELOG_TEST_IDS.FILTERS}
		>
			<div className="flex flex-1 flex-col">
				{filteredChangelogs?.length < 1 ? (
					<Paragraph className="lg:pt-14" data-test-id={CHANGELOG_TEST_IDS.NO_RESULTS}>
						{t('noResults')}
					</Paragraph>
				) : (
					filteredChangelogs.map((entryItem) => (
						<ChangelogItem key={entryItem.id} entryItem={entryItem} />
					))
				)}
			</div>
			<div className="w-full lg:w-71.5 flex flex-col gap-6 lg:pt-14">
				<AppForm form={form} id={filterFormName} onSubmit={() => {}}>
					<AppField
						containerClassName="lg:flex hidden w-full"
						fieldClassName="w-full"
						name="search"
						placeholder={t('searchPlaceholder')}
						iconLeft={<Search />}
						data-test-id={CHANGELOG_TEST_IDS.SEARCH_INPUT}
					/>
					{searchTerm && (
						<GhostButton
							name="category"
							onClick={() => form.setValue('search', '')}
							className="absolute right-3 -translate-y-1/2 top-1/2"
							data-test-id={CHANGELOG_TEST_IDS.SEARCH_CLEAR}
						>
							<Close className="text-neutral-light-600 size-4" />
						</GhostButton>
					)}
				</AppForm>
				<div className="h-px w-full bg-neutral-light-500" />
				<div className="flex flex-wrap gap-2.5 justify-center lg:justify-start">
					<GhostButton
						key={allCategories.id}
						name="category"
						onClick={() => form.setValue('category', allCategories)}
						data-test-id={CHANGELOG_TEST_IDS.CATEGORY_ALL}
					>
						<Tag
							name={allCategories?.name}
							active={selectedCategory?.id === allCategories.id}
							customColors={{
								backgroundColor: allCategories.backgroundColor?.color,
								borderColor: allCategories.borderColor?.color,
							}}
						/>
					</GhostButton>
					{categories?.map((category) => (
						<GhostButton
							key={category.id}
							name="category"
							onClick={() => form.setValue('category', category)}
							data-test-id={CHANGELOG_TEST_IDS.CATEGORY}
						>
							<Tag
								name={category?.name}
								active={selectedCategory?.id === category.id}
								customColors={{
									backgroundColor: category.backgroundColor?.color,
									borderColor: category.borderColor?.color,
								}}
							/>
						</GhostButton>
					))}
				</div>
				<div className="h-px w-full bg-neutral-light-500 lg:flex hidden" />
				<ul className="flex-col gap-2 lg:flex hidden">
					<GhostButton
						name="date"
						className="flex justify-start"
						onClick={() => form.setValue('date', 'all')}
						data-test-id={CHANGELOG_TEST_IDS.FILTERS_ALL}
					>
						<li>
							<Paragraph
								fontSize="bodyRegularLarge"
								className={cn({ 'font-bold': selectedDate === 'all' })}
							>
								{t('allDates')}
							</Paragraph>
						</li>
					</GhostButton>
					{filteredDates.map((date) => (
						<GhostButton
							key={date}
							name="date"
							onClick={() => form.setValue('date', date)}
							data-test-id={CHANGELOG_TEST_IDS.FILTERS_DATE}
						>
							<li>
								<Paragraph
									fontSize="bodyRegularLarge"
									className={cn({ 'font-bold': selectedDate === date })}
								>
									{date}
								</Paragraph>
							</li>
						</GhostButton>
					))}
				</ul>
				<div className="h-px w-full bg-neutral-light-500" />
				<div className="flex flex-col gap-3 w-full lg:max-w-71.5 bg-peach-100 p-5 rounded-xl lg:sticky lg:top-33">
					<Heading tag="h2" fontSize="headingSmall">
						{t('newsletterSubscriptionTitle')}
					</Heading>
					<Paragraph fontSize="bodyRegularMedium">
						{t('newsletterSubscriptionDescription')}
					</Paragraph>
					<MarketoForm
						id={MARKETO_FORM_IDS.NEWSLETTER_SUBSCRIPTION_LOKALOG}
						loadingSkeleton={<NewsletterSubscriptionSkeleton />}
					/>
				</div>
			</div>
		</div>
	)
}

const FilterFormSchema = z.object({
	search: z.string().optional(),
	category: z.any().optional(),
	date: z.any().optional(),
})

type FormSchemaType = typeof FilterFormSchema

export const filterFormName = 'changelogFilter'

const GhostButton = ({
	children,
	name,
	onClick,
	className,
}: {
	children: ReactNode
	name: string
	onClick: () => void
	className?: string
}) => {
	const handleClick = () => {
		onClick()
		// Scroll to top when filter is clicked
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	return (
		<Button
			name={name}
			variant="none"
			size="none"
			className={cn('flex justify-start', className)}
			onClick={handleClick}
		>
			{children}
		</Button>
	)
}

const NewsletterSubscriptionSkeleton = () => (
	<div className="pt-5">
		<Skeleton className="h-11.25 w-full mb-7.75" />
		<Skeleton className="h-9 sm:h-5.75 lg:h-9 w-full mb-5.25 lg:mb-4" />
		<div className="flex flex-col gap-1.5 lg:gap-1.5 mb-3.75 lg:mb-2.5">
			<Skeleton className="h-2.75 lg:h-4 w-full" />
			<Skeleton className="h-2.75 lg:h-4 w-1/2" />
			<Skeleton className="h-2.75 lg:h-4 w-1/3" />
		</div>
		<Skeleton className="h-11.25 w-full rounded-full" />
	</div>
)
